import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import uniqBy from 'lodash/uniqBy';
import type { RootState } from 'store';

import { MifProps, MifStateProps } from 'store/mif/mif.types';

export const initialState: MifStateProps = {
  mifInfo: {
    additionalHealthConditions: [],
    allergyList: [],
    chronicDiseases: [],
    healthConditions: [],
    medications: { prescription: [], supplements: [] },
    preferredPharmacy: '',
    socialHistory: [],
    womenHealth: {
      approxStartDate: '',
      name: '',
      startDate: ''
    }
  },
  mifPicks: {
    allergiesPick: false,
    medicationsPick: false
  },
  mifSurveyAnswers: [],
  semaglutideMif: [],
  tirzepatideMif: []
};

const mifSlice = createSlice({
  initialState,
  name: 'mif',
  reducers: {
    clearMif: (state) => {
      state.mifSurveyAnswers = [];
    },
    clearMifState: (state) => {
      state.mifInfo = initialState.mifInfo;
    },
    clearSemaglutideMif: (state) => {
      state.semaglutideMif = [];
    },
    clearTirzepatideMif: (state) => {
      state.tirzepatideMif = [];
    },
    removePartOfMifAnswers: (state, action: PayloadAction<MifStateProps['mifSurveyAnswers']>) => {
      const filteredArray = state.mifSurveyAnswers.filter((i) => {
        return !action.payload.some((item) => i.question === item.question);
      });
      state.mifSurveyAnswers = filteredArray;
    },
    replaceMif: (state, action: PayloadAction<MifStateProps['mifSurveyAnswers']>) => {
      state.mifSurveyAnswers = action.payload;
    },
    setMif: (state, action: PayloadAction<MifStateProps['mifSurveyAnswers']>) => {
      const filteredArray = uniqBy(
        [...action.payload, ...(state.mifSurveyAnswers ?? [])],
        'question'
      );
      state.mifSurveyAnswers = filteredArray;
    },
    setMifInformation: (state, action: PayloadAction<{ data: unknown; prop: keyof MifProps }>) => {
      const { data, prop } = action.payload;
      (state.mifInfo as Record<string, unknown>)[prop] = data;
    },
    setMifPick: (state, action: PayloadAction<Partial<MifStateProps['mifPicks']>>) => {
      state.mifPicks = { ...state.mifPicks, ...action.payload };
    },
    setSemaglutideMif: (state, action: PayloadAction<MifStateProps['semaglutideMif']>) => {
      const filteredArray = uniqBy(
        [...action.payload, ...(state.semaglutideMif ?? [])],
        'question'
      );
      state.semaglutideMif = filteredArray;
    },
    setTirzepatideMif: (state, action: PayloadAction<MifStateProps['tirzepatideMif']>) => {
      const filteredArray = uniqBy(
        [...action.payload, ...(state.tirzepatideMif ?? [])],
        'question'
      );
      state.tirzepatideMif = filteredArray;
    }
  }
});

export const {
  clearMif,
  clearMifState,
  clearSemaglutideMif,
  clearTirzepatideMif,
  removePartOfMifAnswers,
  replaceMif,
  setMif,
  setMifInformation,
  setMifPick,
  setSemaglutideMif,
  setTirzepatideMif
} = mifSlice.actions;

export const selectMifInfo = (state: RootState) => state.mif;

export default mifSlice.reducer;
